import axios, { AxiosPromise } from 'axios'

import {
  BGPSession,
  Elan,
  IpBandwidth,
  LAG,
  LoaDocument,
  Location,
  SearchResultsLocationSearchResult,
  MetricsTypesResponse,
  Port,
  PortTypeInfo,
  Prefix,
  Product,
  Project,
  ServiceComponent,
  VLAN,
  PeeringDBSizeResponse,
  FlexConnectKeySchema,
  FlexConnectKeyResolvedSchema,
  MetricsTypesGroupedResponse,
  MetricsSeriesResponse,
  BatchMetricQuery,
  BatchMetricsSeriesResponse,
  BatchComponentMetricQuery,
  BatchComponentMetricsSeriesResponse,
  BgpSessionsRollupResponse,
  CustomerReferenceUpdate,
} from '@/lib/definitions/api.types'
import { apiUrl, getAxiosRequestConfigWithHeaders } from '@/lib/misc/Utils'
import {
  ProductComponentIdentifiers,
  ProductIdentifiers,
} from '@/lib/definitions/types'
import {
  GeoCircle,
  GeoRectangle,
} from '@/pages/ServiceWizard/ServiceWizard.types'
import { GenericObject } from '../definitions/generic.types'

export class ServiceService {
  getServices({
    serviceId,
    serviceComponentId,
    productId,
    includeDecommissioned,
    includeCancelled,
    includeResellerCustomers,
  }: {
    serviceId?: string | number
    serviceComponentId?: string | number
    productId?: ProductIdentifiers | ProductIdentifiers[]
    includeDecommissioned?: boolean
    includeCancelled?: boolean
    includeResellerCustomers?: boolean
  }): AxiosPromise<any[]> {
    const urlParams = []
    if (serviceId) urlParams.push(`service_id=${serviceId}`)
    if (serviceComponentId)
      urlParams.push(`service_component_id=${serviceComponentId}`)
    if (productId) urlParams.push(`product_id=${productId}`)
    if (includeDecommissioned) urlParams.push('include_decommissioned=true')
    if (includeCancelled) urlParams.push('include_cancelled=true')
    if (includeResellerCustomers)
      urlParams.push('include_reseller_customers=true')

    return axios.get(
      apiUrl(
        `/api/v1/service/${urlParams.length > 0 ? `?${urlParams.join('&')}` : ''}`
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getService(serviceId: string | number): AxiosPromise<any> {
    return axios.get(
      apiUrl(`/api/v1/service/${serviceId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getAggregatedCommitServiceIDs(
    serviceId: string | number
  ): AxiosPromise<{ portal_ids: string[]; sids: string[] }> {
    return axios.get(
      apiUrl(`/api/v1/service/aggregated-commit/${serviceId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  patchCustomerReference(
    serviceId: string | number,
    payload: CustomerReferenceUpdate
  ): AxiosPromise<unknown> {
    return axios.patch(
      apiUrl(`/api/v1/service/${serviceId}/customer-reference`),
      payload,
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceMetrics(
    serviceId: string | number,
    kind: string,
    timeframe?: string,
    step?: number
  ): AxiosPromise<MetricsSeriesResponse> {
    const params: any = {}
    if (!!timeframe) params.timeframe = timeframe
    if (!!step) params.step = step

    return axios.get(
      apiUrl(`/api/v1/metric/service/${serviceId}/${kind}`, params),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServicesMetrics(
    payload: BatchMetricQuery
  ): AxiosPromise<BatchMetricsSeriesResponse> {
    return axios.post(
      apiUrl('/api/v1/metric/services'),
      payload,
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceComponentsMetrics(
    payload: BatchComponentMetricQuery
  ): AxiosPromise<BatchComponentMetricsSeriesResponse> {
    return axios.post(
      apiUrl('/api/v1/metric/service-components'),
      payload,
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceComponentMetrics(
    serviceComponentId: string | number,
    kind: string,
    timeframe?: string,
    step?: number
  ): AxiosPromise<MetricsSeriesResponse> {
    const params: any = {}
    if (!!timeframe) params.timeframe = timeframe
    if (!!step) params.step = step

    return axios.get(
      apiUrl(
        `/api/v1/metric/service-component/${serviceComponentId}/${kind}`,
        params
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceMetricTypes(
    product_id: string | undefined,
    service_id: number | undefined
  ): AxiosPromise<MetricsTypesResponse> {
    const urlParams = []
    if (product_id) urlParams.push(`product_id=${product_id}`)
    if (service_id) urlParams.push(`service_id=${service_id}`)
    return axios.get(
      apiUrl(`/api/v1/metric/types?${urlParams.join('&')}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceMetricTypesGrouped(
    product_id: string | undefined,
    service_id: number | undefined,
    service_component_id: number | undefined
  ): AxiosPromise<MetricsTypesGroupedResponse> {
    const urlParams: GenericObject = {}
    if (product_id) urlParams.product_id = product_id
    if (service_id) urlParams.service_id = service_id
    if (service_component_id)
      urlParams.service_component_id = service_component_id

    return axios.get(
      apiUrl('/api/v1/metric/types/grouped', urlParams),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getAsnStatistics(asn?: number): AxiosPromise<any> {
    const urlParams = []
    if (asn) urlParams.push(`asn=${asn}`)
    return axios.get(
      apiUrl(
        `/api/v1/metric/asn-statistics${urlParams.length > 0 ? `?${urlParams.join('&')}` : ''}`
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceComponents({
    serviceId,
    excludeDependency,
    pco,
    dependencyId,
    includeServices,
  }: {
    serviceId?: string | number
    excludeDependency?: boolean
    pco?: ProductComponentIdentifiers | ProductComponentIdentifiers[]
    dependencyId?: string | number
    includeServices?: boolean
  }): AxiosPromise<
    | Port[]
    | LAG[]
    | BGPSession[]
    | VLAN[]
    | Prefix[]
    | IpBandwidth[]
    | Elan[]
    | ServiceComponent[]
  > {
    const urlParams = []

    if (serviceId) urlParams.push(`service_id=${serviceId}`)
    if (pco) urlParams.push(`product_component_id=${pco}`)
    if (excludeDependency !== undefined)
      urlParams.push(`exclude_dependency=${excludeDependency}`)
    if (dependencyId) urlParams.push(`dependency_id=${dependencyId}`)
    if (includeServices !== undefined)
      urlParams.push(`include_services=${includeServices}`)
    return axios.get(
      apiUrl(`/api/v1/service-component/?${urlParams.join('&')}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getUsedVlans(serviceId: string | number): AxiosPromise<VLAN[]> {
    return axios.get(
      apiUrl(`/api/v1/service-component/vlans/used?service_id=${serviceId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getServiceComponent(
    serviceComponentId: string | number
  ): AxiosPromise<Port | LAG | ServiceComponent> {
    return axios.get(
      apiUrl(`/api/v1/service-component/${serviceComponentId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getLocations(queryParams?: {
    productId?: number | string
  }): AxiosPromise<Location[]> {
    const { productId } = queryParams || {}
    const urlParams = []

    if (productId) urlParams.push(`product_id=${productId}`)

    return axios.get(
      apiUrl(
        `/api/v1/location/${!!urlParams.length ? `?${urlParams.join('&')}` : ''}`
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  searchLocations(queryParams?: {
    text?: string
    name?: string
    country?: string
    withinCircle?: GeoCircle
    withinRectangle?: GeoRectangle
    productPID?: string
    allowsGRETunnels?: boolean
    portType?: string
    includeExtensions?: boolean
  }): AxiosPromise<SearchResultsLocationSearchResult> {
    const {
      text,
      name,
      country,
      withinCircle,
      withinRectangle,
      productPID,
      allowsGRETunnels,
      portType,
      includeExtensions,
    } = queryParams || {}
    const urlParams = []

    if (text) urlParams.push(`text=${text}`)
    if (name) urlParams.push(`name=${name}`)
    if (country) urlParams.push(`country=${country}`)
    if (portType) urlParams.push(`port_type=${portType}`)
    if (withinCircle) {
      urlParams.push(
        `within_circle=${withinCircle.center.lat},${withinCircle.center.lng},${withinCircle.radius}`
      )
    }
    if (withinRectangle) {
      urlParams.push(
        `within_rectangle=${withinRectangle.southWest.lat},${withinRectangle.southWest.lng},${withinRectangle.northEast.lat},${withinRectangle.northEast.lng}`
      )
    }
    if (productPID) {
      urlParams.push(`product_pid=${productPID}`)
    }
    if (allowsGRETunnels) {
      urlParams.push(`allows_gre_tunnels=${allowsGRETunnels}`)
    }
    if (includeExtensions !== undefined) {
      urlParams.push(`include_extensions=${includeExtensions}`)
    }
    return axios.get(
      apiUrl(
        `/api/v1/location/search${!!urlParams.length ? `?${urlParams.join('&')}` : ''}`
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getPortTypes(): AxiosPromise<PortTypeInfo[]> {
    return axios.get(
      apiUrl('/api/v1/port/types'),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getProducts(): AxiosPromise<Product[]> {
    return axios.get(
      apiUrl('/api/v1/product/'),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getProjects(): AxiosPromise<Project[]> {
    return axios.get(
      apiUrl('/api/v1/project/'),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getLoaDocuments({
    serviceId,
  }: {
    serviceId?: string | number
  }): AxiosPromise<LoaDocument[]> {
    const urlParams = []
    if (serviceId) urlParams.push(`service_id=${serviceId}`)
    return axios.get(
      apiUrl(
        `/api/v1/loa-document/${urlParams.length > 0 ? `?${urlParams.join('&')}` : ''}`
      ),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getAsSetSize(
    sizes: string[]
  ): AxiosPromise<Record<string, PeeringDBSizeResponse>> {
    return axios.get(
      apiUrl(`/api/v1/as-info/as-set/size?as_set=${sizes}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getFlexConnectKey(
    serviceId: string | number
  ): AxiosPromise<FlexConnectKeySchema> {
    return axios.get(
      apiUrl(`/api/v1/service/${serviceId}/flex-connect-key`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  createOrGenerateFlexConnectKey(
    serviceId: string | number
  ): AxiosPromise<FlexConnectKeySchema> {
    return axios.put(
      apiUrl(`/api/v1/service/${serviceId}/flex-connect-key`),
      {},
      getAxiosRequestConfigWithHeaders()
    )
  }

  resolveFlexConnectKey(
    flexConnectKey: string
  ): AxiosPromise<FlexConnectKeyResolvedSchema> {
    return axios.post(
      apiUrl('/api/v1/flex-connect-key/'),
      {
        key: flexConnectKey,
      },
      getAxiosRequestConfigWithHeaders()
    )
  }

  getBgpSessionsRollup(): AxiosPromise<BgpSessionsRollupResponse> {
    return axios.get(
      apiUrl('/api/v1/metric/bgp-sessions'),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getBgpSessionsStats(serviceId: string | number): AxiosPromise<any> {
    return axios.get(
      apiUrl(`/api/v1/metric/service/bgp-sessions-stats/${serviceId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }
}
